import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import request from '../utils/request';
import { 
  countries, 
  statesByCountry, 
  getCountryByCode, 
  getStatesByCountryCode,
  searchStates 
} from '../utils/addressData';

interface Address {
  id?: number;
  firstName: string;
  lastName: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  countryCode?: string;
  isDefault?: boolean;
}

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: Address) => void;
  address?: Address | null; // 如果是编辑模式，传入现有地址
  isEdit?: boolean; // 是否为编辑模式
}

const AddressModal: React.FC<AddressModalProps> = ({
  isOpen,
  onClose,
  onSave,
  address,
  isEdit = false
}) => {
  const [formData, setFormData] = useState<Address>({
    firstName: '',
    lastName: '',
    phone: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    countryCode: '',
    isDefault: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [selectedStateIndex, setSelectedStateIndex] = useState(-1);

  // 当模态框打开或地址数据变化时，更新表单数据
  useEffect(() => {
    if (isOpen && address) {
      setFormData(address);
      setStateSearchTerm(address.state || ''); // 设置搜索词
    } else if (isOpen && !address) {
      // 新增模式，重置表单
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        addressLine1: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        countryCode: '',
        isDefault: false
      });
      setStateSearchTerm(''); // 重置搜索词
    }
    setShowStateDropdown(false); // 隐藏下拉框
    setSelectedStateIndex(-1); // 重置选中索引
  }, [isOpen, address]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log(formData);
    // 验证必填字段
    if (!formData.firstName || !formData.lastName || !formData.phone || !formData.addressLine1 || !formData.city || !formData.state || !formData.zipCode || !formData.country) {
      alert('Please fill in all required fields.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Error saving address. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof Address, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCountryChange = (countryCode: string) => {
    const selectedCountry = getCountryByCode(countryCode);
    setFormData(prev => ({
      ...prev,
      countryCode: countryCode,
      country: selectedCountry?.name || '',
      state: '', // 重置州/省选择
      city: '' // 重置城市选择
    }));
    setStateSearchTerm(''); // 重置搜索词
    setShowStateDropdown(false); // 隐藏下拉框
    setSelectedStateIndex(-1); // 重置选中索引
  };

  const handleStateChange = (stateName: string) => {
    setFormData(prev => ({
      ...prev,
      state: stateName,
      city: '' // 重置城市选择
    }));
    setStateSearchTerm(stateName);
    setShowStateDropdown(false);
  };

  const handleStateSearchChange = (searchTerm: string) => {
    setStateSearchTerm(searchTerm);
    setShowStateDropdown(true);
    setSelectedStateIndex(-1); // 重置选中索引
    
    // 如果搜索词为空，清空选择
    if (searchTerm === '') {
      setFormData(prev => ({
        ...prev,
        state: '',
        city: ''
      }));
    }
  };

  const handleStateKeyDown = (e: React.KeyboardEvent) => {
    const filteredStates = getFilteredStates();
    
    if (!showStateDropdown || filteredStates.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedStateIndex(prev => 
          prev < filteredStates.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedStateIndex(prev => 
          prev > 0 ? prev - 1 : filteredStates.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedStateIndex >= 0 && selectedStateIndex < filteredStates.length) {
          handleStateChange(filteredStates[selectedStateIndex].name);
        }
        break;
      case 'Escape':
        setShowStateDropdown(false);
        setSelectedStateIndex(-1);
        break;
    }
  };

  // 过滤州/省数据
  const getFilteredStates = () => {
    return searchStates(formData.countryCode || '', stateSearchTerm);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            {isEdit ? 'Edit Address' : 'Add New Address'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
            disabled={isSubmitting}
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                First Name *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                placeholder="Enter first name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                placeholder="Enter last name"
                required
              />
            </div>
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Phone Number *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter phone number"
              required
            />
          </div>

          {/* Street Address */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Street Address *
            </label>
            <input
              type="text"
              value={formData.addressLine1}
              onChange={(e) => handleInputChange('addressLine1', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter street address"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                City *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                placeholder="Enter city"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                State/Province *
              </label>
              {formData.countryCode && getStatesByCountryCode(formData.countryCode).length > 0 ? (
                <div className="relative">
                  <input
                    type="text"
                    value={stateSearchTerm}
                    onChange={(e) => handleStateSearchChange(e.target.value)}
                    onKeyDown={handleStateKeyDown}
                    onFocus={() => setShowStateDropdown(true)}
                    onBlur={() => {
                      // 延迟隐藏下拉框，允许点击选项
                      setTimeout(() => setShowStateDropdown(false), 200);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                    placeholder="Search or enter state/province"
                    required
                  />
                  
                  {/* 下拉选项 */}
                  {showStateDropdown && getFilteredStates().length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {getFilteredStates().map((state, index) => (
                        <div
                          key={state.code}
                          className={`px-3 py-2 cursor-pointer text-sm ${
                            index === selectedStateIndex 
                              ? '' 
                              : 'hover:bg-gray-100'
                          }`}
                          style={index === selectedStateIndex ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleStateChange(state.name);
                          }}
                          onMouseEnter={() => setSelectedStateIndex(index)}
                        >
                          {state.name}
                        </div>
                      ))}
                      {getFilteredStates().length === 0 && stateSearchTerm && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          No states found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  placeholder="Enter state/province"
                  required
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                ZIP/Postal Code *
              </label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                placeholder="Enter ZIP code"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Country *
              </label>
              <select
                value={formData.countryCode}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                required
              >
                <option value="">Select a country</option>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isDefault"
              checked={formData.isDefault}
              onChange={(e) => handleInputChange('isDefault', e.target.checked)}
              className="w-4 h-4 border-gray-300 rounded"
              style={{ accentColor: '#2e3a47' } as React.CSSProperties}
            />
            <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
              Set as default address
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#2e3a47' }}
              onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#3a4a57' }}
              onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = '#2e3a47' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (isEdit ? 'Update Address' : 'Add Address')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddressModal;
